import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type {
  DashboardSummary,
  ManagerMetrics,
  PersonMetrics,
  ProjectMetrics,
} from '@blesk/contracts';
import { apiGet } from '../../shared/api';
import { dayStartIso, lastIncludedIso, useUrlFilter, type SectionSearch } from '../../shared/filters';
import { useProjectOptions } from '../../shared/options';
import {
  CHANNEL_LABEL,
  INCIDENT_STATUS_LABEL,
  REQUEST_STATUS_LABEL,
  REQUEST_TYPE_LABEL,
  RUN_STATUS_LABEL,
} from '../../shared/labels';
import { formatDateInProjectTz, formatInProjectTz } from '../../shared/format';
import { useSession } from '../../shared/session';
import { PermissionGate } from '../shell/PermissionGate';
import {
  ChartCard,
  RatingColumns,
  TREND_STEP_HINT,
  TrendLine,
  bucketEndIso,
  bucketLabel,
  percent,
  plural,
  score,
} from '../shell/charts';
import { EmptyRow, ErrorNote, PeriodFilter } from '../shell/ui';

/**
 * §14 ТЗ. Дашборд.
 *
 * Показатели приходят одним запросом: плитки обязаны сходиться между собой,
 * а десять независимых запросов дали бы десять разных «сейчас».
 *
 * Каждая плитка — ссылка в раздел §13 с теми же параметрами отбора, поэтому
 * «из показателя — в список и в карточку» работает без отдельного механизма:
 * это обычный адрес, его можно переслать и положить в закладку.
 *
 * Распределение, динамика и оценки рисуются теми же компонентами, что сводка
 * «Результатов»: один показатель обязан выглядеть одинаково на обоих экранах.
 *
 * Фильтры стоят там, где действуют (§14 «Две группы фильтров»): общие — верхней
 * панелью, «статус опроса» и «негатив» — отдельной подписанной подпанелью
 * (их зона — опросы, оценки, инциденты и разрезы), статус инцидента и тип со
 * статусом обращения — в заголовках своих блоков. Однородная панель из десяти
 * фильтров обещала бы, что каждый сужает весь экран, — это не так.
 */
export function DashboardPage() {
  return (
    <PermissionGate permission="dashboard.read">
      <DashboardInner />
    </PermissionGate>
  );
}

function DashboardInner() {
  const session = useSession();

  const [projectId, setProjectId] = useUrlFilter('projectId');
  const [managerId, setManagerId] = useUrlFilter('managerId');
  const [personId, setPersonId] = useUrlFilter('personId');
  const [channel, setChannel] = useUrlFilter('channel');
  const [runStatus, setRunStatus] = useUrlFilter('runStatus');
  const [hasIncident, setHasIncident] = useUrlFilter('hasIncident');
  const [incidentStatus, setIncidentStatus] = useUrlFilter('incidentStatus');
  const [requestType, setRequestType] = useUrlFilter('requestType');
  const [requestStatus, setRequestStatus] = useUrlFilter('requestStatus');
  const [from, setFrom] = useUrlFilter('from');
  const [to, setTo] = useUrlFilter('to');

  const projects = useProjectOptions();
  const filters = {
    projectId,
    managerId,
    personId,
    channel,
    runStatus,
    hasIncident,
    incidentStatus,
    requestType,
    requestStatus,
    from,
    to,
  };

  const dashboard = useQuery({
    queryKey: ['dashboard', filters],
    queryFn: () => apiGet<DashboardSummary>('/api/dashboard', filters),
    placeholderData: keepPreviousData,
  });

  // §6.1: у объекта один активный менеджер, поэтому список менеджеров
  // собирается из объектов и не требует доступа к разделу «Пользователи»
  const managers = [
    ...new Map(
      (projects.data ?? [])
        .flatMap((project) => (project.manager ? [project.manager] : []))
        .map((manager) => [manager.id, manager]),
    ).values(),
  ].sort((left, right) => left.fullName.localeCompare(right.fullName, 'ru'));

  const data = dashboard.data;

  /**
   * Действующие границы периода. Пустые поля не означают «без периода»:
   * сервер подставляет умолчание §14, и его границы приходят в `range`.
   * Эти же значения стоят в полях «С»/«По» и уходят в ссылки на разделы —
   * иначе плитка «79 Завершено» вела в список из 103 строк: экран показывал
   * цифры одного отбора, а переносил другой (прогон QA 22.08.2026, дефект 3).
   */
  const effectiveFrom = from || (data?.range.from ?? '');
  const effectiveTo = to || (data?.range.to ?? '');

  /**
   * Параметры перехода в раздел §13. Переносится то, что список умеет и
   * показать, и применить: объект, период и статус. Канал, менеджер и
   * администратор остаются уточнением дашборда — фильтр, который в разделе
   * нельзя увидеть и снять, хуже, чем более широкий список.
   */
  const listSearch = (extra: SectionSearch): SectionSearch => {
    const merged: SectionSearch = { projectId, from: effectiveFrom, to: effectiveTo, ...extra };
    return Object.fromEntries(Object.entries(merged).filter(([, value]) => value)) as SectionSearch;
  };
  const narrowedBeyondLists = Boolean(channel || managerId || personId);
  const person = personId
    ? data?.byPerson.items.find((row) => row.personId === personId)
    : undefined;

  return (
    <>
      <h1>Дашборд</h1>
      <p className="muted">
        Показатели считаются по данным сервиса и не зависят от доступности Bitrix24.
      </p>

      <div className="toolbar">
        {/* В полях стоит действующий период, в том числе подставленный сервером:
            работающий отбор должен быть виден там же, где его меняют (дефект 3) */}
        <PeriodFilter from={effectiveFrom} to={effectiveTo} onFrom={setFrom} onTo={setTo} />
        <label>
          Объект
          <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            <option value="">Все</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Менеджер
          <select value={managerId} onChange={(event) => setManagerId(event.target.value)}>
            <option value="">Все</option>
            {managers.map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.fullName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Канал
          <select value={channel} onChange={(event) => setChannel(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(CHANNEL_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="toolbar toolbar--scoped">
        <span className="toolbar__scope">
          Срез опросов
          <small>сужает опросы, оценки, инциденты и разрезы; обращений не касается</small>
        </span>
        <label>
          Статус опроса
          <select value={runStatus} onChange={(event) => setRunStatus(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(RUN_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Негатив
          <select value={hasIncident} onChange={(event) => setHasIncident(event.target.value)}>
            <option value="">Все</option>
            <option value="true">Только с инцидентом</option>
            <option value="false">Без инцидента</option>
          </select>
        </label>
      </div>

      {/* Отбор, который выставляется кнопкой в таблице, обязан быть виден там же,
          где остальные фильтры: иначе он снимается только из той таблицы, до которой
          нужно долистать. */}
      {personId && (
        <div className="chips">
          <span className="chip">
            Администратор: {person?.personName ?? personId}
            <button
              type="button"
              aria-label="Снять отбор по администратору"
              onClick={() => setPersonId('')}
            >
              ✕
            </button>
          </span>
        </div>
      )}

      <ErrorNote error={dashboard.error} />

      {data && (
        <>
          {/* Подпись читает границы там же, где их задаёт `PeriodFilter`, — в UTC:
              пересчёт в пояс сдвинул бы день на смещение зоны. Сверху печатается
              последний включённый момент, а не сама граница полуинтервала,
              иначе отбор «по 22.08» читался бы как «— 23.08». */}
          <p className="muted">
            Период: {formatDateInProjectTz(data.range.from, 'UTC')} —{' '}
            {formatDateInProjectTz(lastIncludedIso(data.range.to), 'UTC')}
            {narrowedBeyondLists &&
              ' · канал, менеджер и администратор в разделы не переносятся: там таких фильтров нет'}
          </p>

          <section className="section-block">
            <h2>Опросы</h2>
            <div className="stat-grid">
              <StatLink
                to="/results"
                search={listSearch({ status: 'SCHEDULED' })}
                value={data.surveys.scheduled}
                label="Запланировано"
              />
              <StatLink
                to="/results"
                search={listSearch({ status: 'SENT' })}
                value={data.surveys.sent}
                label="Отправлено"
              />
              <StatLink
                to="/results"
                search={listSearch({ status: 'COMPLETED' })}
                value={data.surveys.completed}
                label="Завершено"
              />
              <StatLink
                to="/results"
                search={listSearch({ status: 'EXPIRED' })}
                value={data.surveys.expired}
                label="Просрочено"
              />
              <Stat value={percent(data.surveys.responseRate)} label="Доля ответивших" />
              <Stat value={score(data.ratings.average)} label="Средняя оценка" />
            </div>
          </section>

          <section className="section-block">
            <div className="chart-grid">
              <ChartCard
                title="Распределение оценок"
                hint={
                  data.ratings.answers > 0
                    ? plural(data.ratings.answers, ['ответ', 'ответа', 'ответов'])
                    : 'оценок нет'
                }
              >
                <RatingColumns
                  distribution={data.ratings.distribution}
                  answers={data.ratings.answers}
                />
              </ChartCard>

              <ChartCard
                title="Динамика средней оценки"
                hint={TREND_STEP_HINT[data.ratings.trendStep]}
              >
                <TrendLine points={data.ratings.trend} step={data.ratings.trendStep} />
                {/* §14 «из показателя — в список»: переход — обычная ссылка, её можно
                    переслать; точки SVG ссылками быть не могут, поэтому корзины идут
                    рядом отдельным рядом — все, а не только подписанные на оси.
                    Ссылка несёт границы корзины фильтром «С…По» — они видны в разделе
                    и воспроизводят цифру точки один в один (та же механика, что у
                    плиток после дефекта 3). */}
                {data.ratings.trend.length > 0 && (
                  <p className="chart__periods">
                    {data.ratings.trend.map((point) => (
                      <Link
                        key={point.bucket}
                        to="/results"
                        search={listSearch({
                          from: dayStartIso(point.bucket),
                          to: bucketEndIso(point.bucket, data.ratings.trendStep),
                        })}
                      >
                        {bucketLabel(point.bucket, data.ratings.trendStep)}
                      </Link>
                    ))}
                  </p>
                )}
              </ChartCard>
            </div>
          </section>

          <section className="section-block">
            <div className="section-head">
              <h2>Негативные инциденты</h2>
              <label>
                Статус инцидента
                <select
                  value={incidentStatus}
                  onChange={(event) => setIncidentStatus(event.target.value)}
                >
                  <option value="">Все</option>
                  {Object.entries(INCIDENT_STATUS_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="stat-grid">
              <StatLink
                to="/incidents"
                search={listSearch({ status: 'NEW' })}
                value={data.incidents.new}
                label="Новые"
              />
              <StatLink
                to="/incidents"
                search={listSearch({ status: 'IN_PROGRESS' })}
                value={data.incidents.inProgress}
                label="В работе"
              />
              <StatLink
                to="/incidents"
                search={listSearch({ status: 'CLOSED' })}
                value={data.incidents.closed}
                label="Закрытые"
              />
              <StatLink
                to="/incidents"
                search={listSearch({ status: 'REJECTED' })}
                value={data.incidents.rejected}
                label="Отклонённые"
              />
              <StatLink
                to="/incidents"
                search={listSearch({})}
                value={data.incidents.total}
                label="Всего за период"
              />
            </div>
          </section>

          <section className="section-block">
            <div className="section-head">
              <h2>Обращения</h2>
              <label>
                Тип обращения
                <select
                  value={requestType}
                  onChange={(event) => setRequestType(event.target.value)}
                >
                  <option value="">Все</option>
                  {Object.entries(REQUEST_TYPE_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Статус обращения
                <select
                  value={requestStatus}
                  onChange={(event) => setRequestStatus(event.target.value)}
                >
                  <option value="">Все</option>
                  {Object.entries(REQUEST_STATUS_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Два разреза одних и тех же обращений: суммы рядов равны между собой,
                и смешивать их в одну сетку значило бы показать восемь разных чисел.
                Крупная цифра типа — открытые: именно они ждут действия менеджера. */}
            <h3 className="stat-subhead">Открытых по типам</h3>
            <div className="stat-grid">
              {data.requests.byType.map((row) => (
                <StatLink
                  key={row.type}
                  to="/requests"
                  search={listSearch({ type: row.type })}
                  value={row.open}
                  label={`${REQUEST_TYPE_LABEL[row.type]} · всего ${row.total}`}
                />
              ))}
            </div>
            <h3 className="stat-subhead">По статусам</h3>
            <div className="stat-grid">
              {data.requests.byStatus.map((row) => (
                <StatLink
                  key={row.status}
                  to="/requests"
                  search={listSearch({ status: row.status })}
                  value={row.count}
                  label={REQUEST_STATUS_LABEL[row.status]}
                />
              ))}
            </div>
          </section>

          <section className="section-block">
            <h2>
              Открытые жалобы{' '}
              {data.requests.openComplaintsTotal > data.requests.openComplaints.length && (
                <span className="muted">
                  (показаны {data.requests.openComplaints.length} из{' '}
                  {data.requests.openComplaintsTotal})
                </span>
              )}
            </h2>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Номер</th>
                    <th>Объект</th>
                    <th>Администратор</th>
                    <th>Статус</th>
                    <th>Создано</th>
                  </tr>
                </thead>
                <tbody>
                  {data.requests.openComplaints.length === 0 && (
                    <EmptyRow colSpan={5} loading={false} />
                  )}
                  {data.requests.openComplaints.map((complaint) => (
                    <tr key={complaint.id}>
                      <td>
                        {/* Номер обещает конкретную запись, поэтому ведёт по номеру,
                            а не в список «объект + тип + статус», где её искать заново. */}
                        <Link to="/requests" search={{ number: complaint.number }}>
                          {complaint.number}
                        </Link>
                      </td>
                      <td>{complaint.projectName}</td>
                      <td>{complaint.personName}</td>
                      <td>{REQUEST_STATUS_LABEL[complaint.status]}</td>
                      {/* §19: время события — в часовом поясе объекта */}
                      <td>{formatInProjectTz(complaint.createdAt, complaint.projectTimezone)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <ProjectsBlock rows={data.byProject} listSearch={listSearch} />
          <ManagersBlock rows={data.byManager} onSelect={setManagerId} />
          <PeopleBlock
            rows={data.byPerson.items}
            total={data.byPerson.total}
            onSelect={setPersonId}
          />

          {/* §3 против §14: состав дашборда описан для всех, доступ к очереди — нет */}
          {data.integration && (
            <section className="section-block">
              <h2>Синхронизация с Bitrix24</h2>
              <div className="stat-grid">
                {/* Плитка и бейдж режима в шапке выводятся из одних фактов: бейдж — из
                    APP_MODE, плитка — из адаптера. В автономном режиме адаптер бывает
                    только выключенным или тестовым, и плитка говорит это явно, а не
                    противоречит шапке словом «Доступна». */}
                <Stat
                  value={
                    !data.integration.integrationEnabled
                      ? 'Отключена'
                      : data.integration.b24Available
                        ? 'Работает'
                        : 'Недоступна'
                  }
                  label={
                    !data.integration.integrationEnabled
                      ? 'Интеграция · автономный режим'
                      : session.mode === 'STANDALONE'
                        ? 'Интеграция · тестовый адаптер'
                        : 'Интеграция'
                  }
                />
                <StatLink
                  to="/integration"
                  search={{ status: 'PENDING' }}
                  value={data.integration.counts.PENDING}
                  label="В очереди"
                />
                <StatLink
                  to="/integration"
                  search={{ status: 'FAILED' }}
                  value={data.integration.counts.FAILED}
                  label="Неуспешных"
                />
                <StatLink
                  to="/integration"
                  search={{ status: 'NEEDS_ATTENTION' }}
                  value={data.integration.attentionCount}
                  label="Требует внимания"
                />
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

/** Плитка-ссылка: показатель и раздел §13 с тем же отбором — одно и то же место. */
function StatLink({
  to,
  search,
  value,
  label,
}: {
  to: '/results' | '/incidents' | '/requests' | '/integration';
  search: SectionSearch;
  value: number;
  label: string;
}) {
  return (
    <Link to={to} search={search} className="stat stat--link">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </Link>
  );
}

type SortKey = 'average' | 'responseRate';

/**
 * §14: «объекты с наихудшей оценкой и низкой долей ответа» — это две сортировки
 * одного среза, а не два разных показателя. Сортировка на клиенте: объектов
 * не больше сотни (§20), гонять ради этого ещё два запроса незачем.
 *
 * Здесь таблица, а не полосы ProjectBars из «Результатов»: §14 требует
 * «результаты по объектам» — запуски, завершённые, просроченные, — и в полосу
 * одной метрики они не помещаются. Ранжирование даёт сортировка.
 */
function ProjectsBlock({
  rows,
  listSearch,
}: {
  rows: ProjectMetrics[];
  listSearch: (extra: SectionSearch) => SectionSearch;
}) {
  const [sort, setSort] = useState<SortKey>('average');

  // Объекты без данных уходят вниз: «нет оценок» — не то же, что «плохие оценки»
  const sorted = [...rows].sort((left, right) => {
    const a = left[sort];
    const b = right[sort];
    if (a === null && b === null) return left.projectName.localeCompare(right.projectName, 'ru');
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  });

  return (
    <section className="section-block">
      <div className="section-head">
        <h2>Объекты</h2>
        <label>
          Сначала
          <select value={sort} onChange={(event) => setSort(event.target.value as SortKey)}>
            <option value="average">с худшей оценкой</option>
            <option value="responseRate">с низкой долей ответа</option>
          </select>
        </label>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Объект</th>
              <th>Запусков</th>
              <th>Завершено</th>
              <th>Просрочено</th>
              <th>Доля ответивших</th>
              <th>Средняя оценка</th>
              <th>Инцидентов</th>
              <th>Открытых</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <EmptyRow colSpan={9} loading={false} />}
            {sorted.map((row) => (
              <tr key={row.projectId}>
                <td>{row.projectName}</td>
                <td>{row.runs}</td>
                <td>{row.completed}</td>
                <td>{row.expired}</td>
                <td>{percent(row.responseRate)}</td>
                <td>{score(row.average)}</td>
                <td>{row.incidents}</td>
                <td>{row.openIncidents}</td>
                <td>
                  <Link to="/results" search={listSearch({ projectId: row.projectId })}>
                    Результаты
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ManagersBlock({
  rows,
  onSelect,
}: {
  rows: ManagerMetrics[];
  onSelect: (managerId: string) => void;
}) {
  return (
    <section className="section-block">
      <h2>Менеджеры</h2>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Менеджер</th>
              <th>Объектов</th>
              <th>Запусков</th>
              <th>Завершено</th>
              <th>Доля ответивших</th>
              <th>Средняя оценка</th>
              <th>Открытых инцидентов</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow colSpan={8} loading={false} />}
            {rows.map((row) => (
              <tr key={row.managerId ?? 'none'}>
                <td>{row.managerName}</td>
                <td>{row.projects}</td>
                <td>{row.runs}</td>
                <td>{row.completed}</td>
                <td>{percent(row.responseRate)}</td>
                <td>{score(row.average)}</td>
                <td>{row.openIncidents}</td>
                <td>
                  {/* Тот же жест, что у администраторов: разрез — в фильтр дашборда.
                      У строки «без менеджера» фильтровать не по чему. */}
                  {row.managerId && (
                    <button type="button" onClick={() => onSelect(row.managerId!)}>
                      Только этот
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PeopleBlock({
  rows,
  total,
  onSelect,
}: {
  rows: PersonMetrics[];
  total: number;
  onSelect: (personId: string) => void;
}) {
  return (
    <section className="section-block">
      <h2>
        Администраторы заказчика{' '}
        {total > rows.length && (
          <span className="muted">
            (показаны {rows.length} из {total}, начиная с худших оценок)
          </span>
        )}
      </h2>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Администратор</th>
              <th>Объектов</th>
              <th>Запусков</th>
              <th>Завершено</th>
              <th>Доля ответивших</th>
              <th>Средняя оценка</th>
              <th>Инцидентов</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow colSpan={8} loading={false} />}
            {rows.map((row) => (
              <tr key={row.personId}>
                <td>{row.personName}</td>
                <td>{row.projects}</td>
                <td>{row.runs}</td>
                <td>{row.completed}</td>
                <td>{percent(row.responseRate)}</td>
                <td>{score(row.average)}</td>
                <td>{row.incidents}</td>
                <td>
                  <button type="button" onClick={() => onSelect(row.personId)}>
                    Только этот
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
